import { useSearchParams } from 'react-router-dom';
import { Bot, MessageCircleQuestion, NotebookPen } from 'lucide-react';
import { AssistantChat } from '@speles7172/assistant-console';
import '@speles7172/assistant-console/styles.css';
import { assistantTransport } from '../api/assistant';
import { TabNav } from '../components/common/TabNav';
import { AssistantBriefingCard } from '../components/assistant/AssistantBriefingCard';
import { RecordNotes } from '../components/assistant/RecordNotes';
import { useAssistantNavigation } from '../components/assistant/useAssistantNavigation';

const TABS = ['ask', 'notes'] as const;
type Tab = (typeof TABS)[number];

/** What a tool is called in "Looked at: …". */
const TOOL_LABELS: Record<string, string> = {
  search: 'search',
  list_events: 'events',
  event_overview: 'event overview',
  list_tasks: 'tasks',
  guest_summary: 'guests',
  expenses: 'expenses',
  client_ledger: 'client payments',
  event_files: 'files',
  photos: 'photos',
  conversations: 'conversations',
  inquiries: 'enquiries',
  notes: 'notes',
};

const SUGGESTIONS = {
  en: [
    'What events do we have in the next 30 days?',
    'Which vendor payments are overdue?',
    'Show a chart of guests per event this month',
    'What did clients write to us this week?',
  ],
  he: [
    'אילו אירועים יש לנו בחודש הקרוב?',
    'אילו תשלומים לספקים באיחור?',
    'הראה גרף של מספר האורחים לפי אירוע',
  ],
};

/**
 * The AI assistant (producers and administrators): ask a question of the
 * office's data, or keep notes the assistant summarises, links to events,
 * clients and venues, and turns into tasks. The briefing leads, as on the
 * dashboard; the tab is in the address (`?tab=`).
 */
export default function AssistantPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as Tab)
    : 'ask';
  const { onOpen, onActionDone, language } = useAssistantNavigation();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Bot size={24} className="text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assistant</h1>
          <p className="text-sm text-gray-500">
            Ask about your events, money, tasks and conversations — in English or Hebrew — or keep
            notes it turns into actions.
          </p>
        </div>
      </div>

      <AssistantBriefingCard />

      <TabNav
        ariaLabel="Assistant"
        items={[
          { id: 'ask', label: 'Ask', icon: <MessageCircleQuestion size={16} /> },
          { id: 'notes', label: 'Notes', icon: <NotebookPen size={16} /> },
        ]}
        activeId={tab}
        onSelect={(id) =>
          setParams((current) => {
            const next = new URLSearchParams(current);
            next.set('tab', id);
            return next;
          })
        }
      />

      {tab === 'ask' ? (
        <div className="peles-controls">
          <AssistantChat
            transport={assistantTransport}
            onOpen={onOpen}
            onActionDone={onActionDone}
            suggestions={SUGGESTIONS[language]}
            toolLabels={TOOL_LABELS}
            language={language}
            theme="light"
          />
        </div>
      ) : (
        <RecordNotes />
      )}
    </div>
  );
}
